/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Countdown, { CountdownRendererFn } from 'react-countdown';
import { useForm } from 'react-hook-form';
import ReactCodeInput from 'react-code-input';

import mailImg from '../../../assets/auth/mail.svg';
import { Input } from '../../../components';
import {
  useGetAuthConfigurationQuery,
  useForgetPasswordMutation,
  useResetPasswordMutation,
  useVerifyAccountMutation,
  useLoginMutation,
} from '../../../services';
// Canonical envelope helpers. These used to be module-local copies here; they
// now live in src/utils/api-response.ts so every page shares ONE correct
// implementation. That module's header documents what baseQueryWithReAuth
// already reports globally (HTTP 400/500, errorCode 1030) — read it before
// adding a toast to any call site in this file.
import {
  MutationResult,
  classifyMutationResult,
  extractResponsePayload,
  isSuccess,
} from '../../../utils/api-response';
import { PasswordChanged, ResetPassword } from '../components';
import {
  Box,
  Heading,
  Text,
  Link as ChakraLink,
  Button,
} from '@chakra-ui/react';

// CompanyAuthService.forgetPassword catches a mail-send exception and returns
// this marker inside an HTTP 200 body. It is the ONLY business outcome that
// proves no code was ever sent — see the anti-enumeration note on requestCode.
//
// Match on `message`, NOT on `errorCode`: the backend builds this body with
// prepareCustomErrorAppResponse, which puts 'EMAIL FAILED' in `message` and the
// generic CUSTOM_ERROR code '1030' in `errorCode`. Every custom error in that
// API carries 1030 (an invalid/expired reset token does too), so matching the
// code would stop the flow on failures that have nothing to do with delivery.
const EMAIL_FAILED_MESSAGE = 'EMAIL FAILED';

// USER-FACING FAILURE COPY — fixed strings, never the server's words.
//
// Every failure branch in this flow renders one of these three constants and
// NOTHING from the response body. `payload.details` on these endpoints can
// carry exception-derived text (raw SMTP errors on the request step) and, on
// the verify/reset steps, distinguishes server-side token states from one
// another — echoing either hands an anonymous caller an oracle and leaks
// internals. Same copy discipline NewPassword.tsx uses. The body is still
// READ where a named business outcome must be matched (EMAIL_FAILED_MESSAGE
// below); it is never DISPLAYED.
const SEND_CODE_FAILED_MESSAGE =
  'We could not send the verification code right now. Please try again in a moment.';
const VERIFY_CODE_FAILED_MESSAGE =
  'That code is invalid or has expired. Please try again.';
const RESET_PASSWORD_FAILED_MESSAGE =
  'The password reset link is invalid or has expired. Please request a new code.';

export const ForgetPassword = () => {
  const { state } = useLocation() as { state: string };

  const [content, setContent] = useState<
    'forgetPassword' | 'verifyAccount' | 'resetPassword' | 'passwordChanged'
  >('forgetPassword');

  const [isPasswordMatch, setIsPasswordMatch] = useState(true);
  // Single-use reset token returned by verifyAccount; the only proof of a
  // verified OTP that resetPassword will accept.
  const [resetToken, setResetToken] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const navigate = useNavigate();
  const [forgetPassword, { isLoading: isForgetPasswordLoading }] =
    useForgetPasswordMutation();
  const [verifyAccount, { isLoading: isVerifyAccountLoading }] =
    useVerifyAccountMutation();
  const [resetPassword, { isLoading: isResetPasswordLoading }] =
    useResetPasswordMutation();
  const [login] = useLoginMutation();
  const { data } = useGetAuthConfigurationQuery();

  const emailValidation = data?.data
    ? data?.data?.EMAIL_LOGIN_REGEX?.value
    : undefined;

  const {
    register,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
    setFocus,
  } = useForm<{
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
  }>({
    defaultValues: {
      email: state,
    },
  });

  // Request (or resend) the OTP email.
  //
  // The backend reports business failures INSIDE an HTTP 200 body, so
  // .unwrap() does not reject on them — branch on the classified envelope with
  // the same shared helpers onVerifyAccount uses.
  //
  // ANTI-ENUMERATION, DELIBERATE: every business outcome EXCEPT a real
  // delivery failure advances to the OTP screen. An unregistered address
  // comes back as an invalid-credentials custom error (errorCode 1027) and
  // must stay indistinguishable from a successful send, or this endpoint
  // becomes an account-enumeration oracle. Only two things stop the flow, and
  // neither depends on whether the address exists: message 'EMAIL FAILED' (the
  // backend caught a send exception, so no code exists to type in) and a
  // transport error (the request never got an answer at all). Note this is why
  // the test below reads `message` and not `errorCode` — 1027 and the mail
  // failure's 1030 are both just CUSTOM_ERROR, and neither code identifies the
  // outcome.
  //
  // Returns true when the flow advanced / the code was sent.
  const requestCode = async (): Promise<boolean> => {
    setErrorMessage('');

    const response = await forgetPassword({ email: getValues('email') });
    const payload = extractResponsePayload(response);
    const outcome = classifyMutationResult(response);

    const deliveryFailed =
      outcome === MutationResult.TRANSPORT_FAILURE ||
      (outcome === MutationResult.BODY_FAILURE &&
        payload.message === EMAIL_FAILED_MESSAGE);

    if (deliveryFailed) {
      // Fixed, address-independent copy: never echo payload.details here — on
      // EMAIL FAILED the backend puts the raw SMTP exception text in it.
      setErrorMessage(SEND_CODE_FAILED_MESSAGE);
      // No setContent: on the request step this keeps the user on the email
      // form, and on a failed resend it leaves them on the OTP screen — the
      // error banner renders on both.
      return false;
    }

    setContent('verifyAccount');
    return true;
  };

  const onVerifyAccount = async () => {
    setErrorMessage('');
    const response = await verifyAccount({
      email: getValues('email'),
      otp: getValues('otp'),
    });

    if (isSuccess(response) && 'data' in response) {
      setResetToken(response.data.data.resetToken);
      setContent('resetPassword');
      return;
    }

    // Fixed copy for EVERY non-success reply. The body is not read at all
    // here: a wrong OTP, an expired OTP and an address that never had one
    // minted must stay indistinguishable, and `details` on this endpoint can
    // carry exception-derived text.
    setErrorMessage(VERIFY_CODE_FAILED_MESSAGE);
  };

  // Reset submit: build the body explicitly so only email, password and the
  // reset token are sent — the otp/confirmPassword form fields never leave.
  const onSubmit = async () => {
    const matched = getValues('password') === getValues('confirmPassword');
    setIsPasswordMatch(matched);
    if (!matched) return setFocus('confirmPassword');

    setErrorMessage('');
    const response = await resetPassword({
      email: getValues('email'),
      password: getValues('password'),
      resetToken,
    });

    if (isSuccess(response)) {
      setContent('passwordChanged');

      // TODO: add lotti animation
      //
      // Convenience auto-login after a successful reset. Business failures
      // ride inside HTTP 200 bodies, so .unwrap() cannot reject on them — it
      // used to "fail" only by accident, when destructuring a null/empty
      // `data`. Branch on the body with the same isSuccess helper the rest of
      // this flow uses, and treat ANY non-success as "send them to /login with
      // the password they just set" rather than throwing inside a timer, where
      // nothing can catch it. The reset itself already succeeded; a failed
      // auto-login is a convenience miss, not a failed reset.
      setTimeout(async () => {
        try {
          const loginResponse = await login({
            email: getValues('email'),
            password: getValues('password'),
          });

          if (!isSuccess(loginResponse) || !('data' in loginResponse)) {
            navigate('/login');
            return;
          }

          const { accessToken, refreshToken } = loginResponse.data.data;

          if (!accessToken || !refreshToken) {
            navigate('/login');
            return;
          }

          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', refreshToken);

          navigate('/');
        } catch {
          navigate('/login');
        }
      }, 3000);
      return;
    }

    // Rejected reset (expired / already-used / invalid token): surface a
    // visible error and send the user back to request a fresh code — the
    // spent token cannot be reused. One fixed string for all three server
    // token states, and never `details`, which names which one it was.
    setResetToken('');
    setErrorMessage(RESET_PASSWORD_FAILED_MESSAGE);
    setContent('forgetPassword');
  };

  const errorAlert = errorMessage ? (
    <Box
      role="alert"
      mb="6"
      px="4"
      py="3"
      bg="red.50"
      borderWidth="1px"
      borderColor="red.200"
      borderRadius="md"
    >
      <Text color="red.600" fontSize="sm">
        {errorMessage}
      </Text>
    </Box>
  ) : null;

  const renderer: CountdownRendererFn = ({ seconds, completed, api }) => {
    if (completed) {
      // TODO: discuss the best option for this case and similar?
      return (
        <button
          className="text-primary"
          onClick={() => {
            // Resend goes through requestCode so a failed resend surfaces the
            // same visible error. Only restart the 59s cooldown when the mail
            // actually went out — otherwise leave Resend clickable so the user
            // can retry immediately instead of waiting on a send that failed.
            void requestCode().then(sent => {
              if (sent) {
                api.stop();
                api.start();
              }
            });
          }}
        >
          Resend
        </button>
      );
    } else {
      return (
        <Text as="span" color="custom_orange.2">
          00:{seconds} Sec
        </Text>
      );
    }
  };

  if (content === 'forgetPassword') {
    return (
      <Box textAlign="center" w="32rem" mx="auto">
        <Heading
          as="h2"
          fontSize={{ base: '2xl', '2xl': '3xl' }}
          mb="4"
          textAlign="center"
          color="custom_blue.1"
          fontWeight="bold"
          className="2xl:text-3xl"
        >
          Forgot password?
        </Heading>
        <Text as="p" mb="6" textAlign="center" textColor="custom_gray.1">
          Please enter the email address that you should receive the code on.
        </Text>
        <Box>
          {errorAlert}
          <Box mb="6">
            <Input
              required
              type="text"
              name="email"
              id="email"
              label="Email"
              placeholder="Enter your email"
              register={register}
              prefixIcon={mailImg}
              isError={errors.email?.type === 'pattern'}
              pattern={
                new RegExp(
                  emailValidation
                    ? emailValidation.slice(1, emailValidation.length - 1)
                    : '',
                  'g'
                )
              }
            />
          </Box>

          <Box mt="6" w="full" onClick={() => void requestCode()}>
            <Button
              type="button"
              bg="custom_orange.2"
              color="white"
              w="full"
              _hover={{
                bg: 'custom_orange.2',
              }}
              isLoading={isForgetPasswordLoading}
            >
              Continue
            </Button>
          </Box>
          <Text mt="4" textAlign="center" color="custom_blue.1" fontSize="sm">
            Remember your password?{' '}
            <ChakraLink as={Link} color="custom_orange.2" to="/login">
              Back to login
            </ChakraLink>
          </Text>
        </Box>
      </Box>
    );
  } else if (content === 'verifyAccount') {
    return (
      <Box w="32rem" maxW="sm">
        <Heading
          as="h2"
          fontSize="2xl"
          mb="3"
          textAlign="center"
          color="custom_blue.1"
          fontWeight="bold"
          className="2xl:text-3xl"
        >
          Verify account
        </Heading>
        <Text color="custom_gray.1" mb="7" textAlign="center">
          We’ve sent a 6-digit verification code to{' '}
          {getValues('email') || state}
        </Text>
        <Box>
          {errorAlert}
          <Box mb="5">
            <ReactCodeInput
              placeholder="*"
              type="text"
              fields={6}
              inputMode="numeric"
              name="otp"
              onChange={value => setValue('otp', value)}
            />
          </Box>

          <Box mt="6" w="full" onClick={() => void onVerifyAccount()}>
            <Button
              type="submit"
              bg="custom_orange.2"
              color="white"
              w="full"
              _hover={{
                bg: 'custom_orange.2',
              }}
              isLoading={isVerifyAccountLoading}
            >
              Verify
            </Button>
          </Box>

          <Text mt="4" textAlign="center" color="custom_blue.1" fontSize="md">
            Don’t receive code?{' '}
            <Countdown
              date={Date.now() + 59000}
              autoStart={true}
              renderer={renderer}
            />
          </Text>
        </Box>
      </Box>
    );
  } else if (content === 'resetPassword') {
    return (
      <ResetPassword
        register={register}
        isPasswordMatch={isPasswordMatch}
        onSubmit={handleSubmit(onSubmit)}
        isLoading={isResetPasswordLoading}
      />
    );
  } else {
    return <PasswordChanged />;
  }
};
